import { computed, watch, type Ref } from 'vue';

import { useNuxtApp } from '#app';
import type { Product } from '@/_api/models/product';
import type { FilterConfig, FilterState } from '@/_api/types/interfaces/filter';

import { useFilterController, type FilterControllerReturn } from '../useFilterController';

export interface ProductFacetValue {
  id: number | null;
  name: string;
}

type ProductFacetKey = 'category' | 'brand' | 'supplier';

interface ProductFacetDefinition {
  key: ProductFacetKey;
  idKey: string;
  label: string;
  icon: string;
  noneLabel: string;
}

export interface ProductPickerFilterReturn extends FilterControllerReturn<Product> {
  facetKeys: ProductFacetKey[];
}

const facetValue = (
  product: Product,
  { key, idKey }: ProductFacetDefinition,
): ProductFacetValue | null => {
  const related = (product as any)?.[key];
  const id = related?.[idKey];

  if (related == null || id == null) return null;

  return { id: Number(id), name: related.name ?? '' };
};

const facetValueId = (value: ProductFacetValue | null): number | null => value?.id ?? null;

export const useProductPickerFilters = (
  products: Ref<Product[]>,
): ProductPickerFilterReturn => {
  const { $i18n } = useNuxtApp() as any;

  const definitions: ProductFacetDefinition[] = [
    {
      key: 'category',
      idKey: 'idProductCategory',
      label: $i18n.t('general.category'),
      icon: 'lucide:folder',
      noneLabel: $i18n.t('general.noCategory'),
    },
    {
      key: 'brand',
      idKey: 'idProductBrand',
      label: $i18n.t('general.brand'),
      icon: 'lucide:tag',
      noneLabel: $i18n.t('product.filters.noBrand'),
    },
    {
      key: 'supplier',
      idKey: 'idSupplier',
      label: $i18n.t('general.supplier'),
      icon: 'lucide:truck',
      noneLabel: $i18n.t('product.filters.noSupplier'),
    },
  ];

  const selectedIds = (value: any): (number | null)[] => {
    if (!Array.isArray(value)) return [];
    return value.map((item: ProductFacetValue) => facetValueId(item));
  };

  const matchesFacet = (
    product: Product,
    definition: ProductFacetDefinition,
    value: any,
  ): boolean => {
    const ids = selectedIds(value);
    if (!ids.length) return true;
    return ids.includes(facetValueId(facetValue(product, definition)));
  };

  const valueLabel = (value: any): string | null => {
    if (!Array.isArray(value) || !value.length) return null;

    const names = value
      .slice(0, 2)
      .map((item: ProductFacetValue) => item?.name)
      .join($i18n.t('general.listSeparator'));

    if (value.length > 2) {
      return $i18n.t('general.andMore', { list: names, count: value.length - 2 });
    }

    return names;
  };

  let states: Ref<Record<string, FilterState>>;

  const optionCount = (definition: ProductFacetDefinition) => (option: any): number | undefined => {
    const optionId = facetValueId(option?.value);

    return products.value.filter((product) => {
      if (facetValueId(facetValue(product, definition)) !== optionId) return false;

      return definitions.every((other) => {
        if (other.key === definition.key) return true;
        return matchesFacet(product, other, states.value[other.key]?.value);
      });
    }).length;
  };

  const buildFilter = (definition: ProductFacetDefinition): FilterConfig<Product> => ({
    type: definition.key,
    label: definition.label,
    icon: definition.icon,
    kind: 'list',
    strategy: 'local',
    defaultValue: [],
    options: [],
    optionCount: optionCount(definition),
    isEmpty: (value: any) => !Array.isArray(value) || value.length === 0,
    valueLabel,
    matches: (items: Product[], value: any) =>
      items.filter((product) => matchesFacet(product, definition, value)),
  });

  const facetOptions = computed<Record<ProductFacetKey, any[]>>(() => {
    const result = {} as Record<ProductFacetKey, any[]>;

    for (const definition of definitions) {
      const values = new Map<number, ProductFacetValue>();
      let hasNone = false;

      for (const product of products.value) {
        const value = facetValue(product, definition);

        if (!value) {
          hasNone = true;
          continue;
        }

        if (!values.has(value.id as number)) {
          values.set(value.id as number, value);
        }
      }

      const options = [...values.values()]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((value) => ({ label: value.name, value }));

      result[definition.key] = hasNone && options.length
        ? [{ label: definition.noneLabel, value: { id: null, name: definition.noneLabel } }, ...options]
        : options;
    }

    return result;
  });

  const controller = useFilterController<Product>({
    filters: definitions.reduce((filters: Record<string, FilterConfig<Product>>, definition) => {
      filters[definition.key] = buildFilter(definition);
      return filters;
    }, {}),
    categories: definitions.map((definition) => ({
      label: definition.label,
      filters: [definition.key],
    })),
  });

  states = controller.filterStates;

  watch(
    facetOptions,
    (options) => {
      for (const definition of definitions) {
        const state = states.value[definition.key];
        if (state) state.options = options[definition.key];
      }
    },
    { immediate: true },
  );

  return {
    ...controller,
    facetKeys: definitions.map((definition) => definition.key),
  };
};
